import jsPDF from 'jspdf';
import { Invoice, Workspace } from '../types';
import { storage } from '../lib/firebase';
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage';

// Constants for A4 page dimensions in mm
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 20;
const CONTENT_WIDTH = PAGE_WIDTH - (MARGIN * 2);
const VITE_APP_URL = import.meta.env.VITE_APP_URL;

// Logo dimensions
const MAX_LOGO_SIZE = 35;

interface ImageData {
  blob: Blob;
  objectUrl: string;
  width: number;
  height: number;
}

export const getCurrencySymbol = (currency: string): string => {
  const symbols: { [key: string]: string } = {
    'USD': '$',
    'EUR': 'EUR ',
    'GBP': 'GBP ',
    'QAR': 'QAR ',
    'AED': 'AED ',
    'SAR': 'SAR ',
    'INR': 'RS ',
    'JPY': '¥',
    'CNY': '¥',
    'AUD': 'A$',
    'CAD': 'C$',
    'NZD': 'NZ$',
    'SGD': 'S$',
    'HKD': 'HK$',
    'KRW': '₩',
    'BRL': 'R$',
    'ZAR': 'R',
    'RUB': '₽',
    'CHF': 'CHF ',
    'SEK': 'kr ',
    'NOK': 'kr ',
    'DKK': 'kr ',
    'PLN': 'zł',
    'TRY': '₺',
    'MXN': 'Mex$',
    'IDR': 'Rp ',
    'MYR': 'RM ',
    'PHP': '₱',
    'THB': '฿',
    'VND': '₫',
    'EGP': 'E£',
    'PKR': '₨',
    'BDT': '৳',
    'NGN': '₦',
    'GHS': 'GH₵',
    'KES': 'KSh ',
    'UGX': 'USh ',
    'TZS': 'TSh ',
    'RWF': 'RF ',
    'BIF': 'FBu ',
    'ETB': 'Br ',
    'XOF': 'CFA ',
    'XAF': 'FCFA ',
  };
  return symbols[currency] || `${currency} `;
};

const formatCurrency = (amount: number, currency: string) => {
  const symbol = getCurrencySymbol(currency);
  
  // Format based on currency characteristics
  let formattedAmount = amount.toFixed(2);
  
  // Special cases for certain currencies
  switch(currency) {
    case 'JPY':
    case 'KRW':
    case 'VND':
      formattedAmount = Math.round(amount).toString();
      break;
    case 'BHD':
    case 'KWD':
    case 'OMR':
      formattedAmount = amount.toFixed(3);
      break;
  }

  // Add thousand separators
  formattedAmount = formattedAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Position the symbol based on currency convention
  const symbolAfter = ['kr', 'Kč', 'Ft'].some(s => symbol.trim() === s);
  return symbolAfter ? `${formattedAmount} ${symbol}` : `${symbol}${formattedAmount}`;
};



const calculateLogoSize = (width: number, height: number) => {
  const aspectRatio = width / height;
  
  let dimensions;
  if (aspectRatio > 1) {
    // Wider than tall
    dimensions = {
      width: MAX_LOGO_SIZE,
      height: MAX_LOGO_SIZE / aspectRatio
    };
  } else {
    // Taller than wide or square
    dimensions = {
      width: MAX_LOGO_SIZE * aspectRatio,
      height: MAX_LOGO_SIZE
    };
  }
  
  return dimensions;
};

const loadImage = async (url: string): Promise<ImageData> => {
  if (!url) {
    throw new Error('No image URL provided');
  }

  try {
    let imageBlob: Blob;
    
    if (url.startsWith('data:image')) {
      // For base64 strings, create a blob directly
      const byteString = atob(url.split(',')[1]);
      const mimeType = url.split(',')[0].split(':')[1].split(';')[0];
      
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      
      imageBlob = new Blob([ab], { type: mimeType });
    } else {
      // Handle both absolute URLs and relative paths
      const imageUrl = url.startsWith('http') ? url : window.location.origin + url;
      console.debug('[PDF] Fetching image from:', imageUrl);
      const response = await fetch(imageUrl, {
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status} ${response.statusText}`);
      }
      
      imageBlob = await response.blob();
    }

    // Validate that it's actually an image
    if (!imageBlob.type.startsWith('image/')) {
      throw new Error(`Invalid image format: ${imageBlob.type}`);
    }

    // Create a blob URL for the image
    const objectUrl = URL.createObjectURL(imageBlob);

    // Load the image to verify it's valid and get dimensions
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        resolve({
          blob: imageBlob,
          objectUrl,
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image'));
      };

      img.src = objectUrl;
    });
  } catch (error) {
    console.error('Error loading image:', error);
    throw error;
  }
};

const safeText = (text: string | null | undefined): string => {
  if (!text) return '';
  return text.replace(/[^\x00-\x7F]/g, ''); // Remove non-ASCII characters
};

// Template function type
export type InvoicePDFTemplate = (invoice: Invoice, workspace: Workspace) => Promise<jsPDF>;

// Default template (current implementation, refactored into a function)
async function defaultInvoiceTemplate(invoice: Invoice, workspace: Workspace): Promise<jsPDF> {
  // Initialize PDF with A4 size
  const pdf = new jsPDF({
    format: 'a4',
    unit: 'mm',
    orientation: 'portrait'
  });

  // Always use invoice.currency if present, otherwise workspace or USD
  const currency = invoice.currency || workspace.settings?.currency || 'USD';

  // Get brand color (default to blue if not set)
  const brandColor = workspace.settings?.branding?.primaryColor || '#3B82F6';
  const hexToRgb = (hex: string): [number, number, number] => {
    let c = hex.replace('#', '');
    if (c.length === 3) c = c.split('').map(x => x + x).join('');
    const num = parseInt(c, 16);
    return [num >> 16, (num >> 8) & 255, num & 255];
  };
  const brandRGB: [number, number, number] = hexToRgb(brandColor);

  try {
    console.log('[PDF] Starting PDF generation for invoice:', invoice.invoiceNumber);

    // Set default font
    pdf.setFont('helvetica');

    // Header Section
    let yPos = MARGIN;

    // Company Logo - Right side
    if (workspace.logo) {
      try {
        const imageData = await loadImage(workspace.logo);
        const logoSize = calculateLogoSize(imageData.width, imageData.height);
        const logoX = PAGE_WIDTH - MARGIN - logoSize.width;
        const logoY = yPos;
        pdf.addImage(
          imageData.objectUrl,
          'PNG',
          logoX,
          logoY,
          logoSize.width,
          logoSize.height,
          undefined,
          'FAST'
        );
        URL.revokeObjectURL(imageData.objectUrl);
      } catch (error) {
        // Draw placeholder
        const placeholderSize = MAX_LOGO_SIZE;
        const placeholderX = PAGE_WIDTH - MARGIN - placeholderSize;
        pdf.setDrawColor(200, 200, 200);
        pdf.setFillColor(240, 240, 240);
        pdf.rect(placeholderX, yPos, placeholderSize, placeholderSize, 'FD');
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text('LOGO', placeholderX + placeholderSize/2, yPos + placeholderSize/2, { align: 'center' });
        pdf.setTextColor(0);
      }
    }

    // Company Name - Left side
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...brandRGB);
    const companyName = safeText(workspace.settings?.branding?.companyName || workspace.name);
    pdf.text(companyName, MARGIN, yPos + 8);
    pdf.setTextColor(0);

    // Company Details - Left side
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    yPos += 15;
    if (workspace.settings?.branding?.licenseNumber) {
      pdf.text(`License Number: ${safeText(workspace.settings.branding.licenseNumber)}`, MARGIN, yPos);
      yPos += 6;
    }
    const address = workspace.settings?.branding?.address || '';
    if (address) {
      const addressLines = address.split('\n');
      addressLines.forEach(line => {
        if (line.trim()) {
          pdf.text(safeText(line.trim()), MARGIN, yPos);
          yPos += 6;
        }
      });
    }
    if (workspace.settings?.branding?.phone) {
      pdf.text(`Phone: ${safeText(workspace.settings.branding.phone)}`, MARGIN, yPos);
      yPos += 6;
    }
    if (workspace.settings?.branding?.mobile) {
      pdf.text(`Mobile: ${safeText(workspace.settings.branding.mobile)}`, MARGIN, yPos);
      yPos += 6;
    }
    if (workspace.settings?.branding?.email) {
      pdf.text(safeText(workspace.settings.branding.email), MARGIN, yPos);
      yPos += 6;
    }
    if (workspace.settings?.branding?.website) {
      pdf.setTextColor(59, 130, 246);
      pdf.text(safeText(workspace.settings.branding.website), MARGIN, yPos);
      pdf.setTextColor(0);
      yPos += 6;
    }

    // Horizontal Line
    yPos += 8;
    pdf.setDrawColor(0);
    pdf.setLineWidth(0.5);
    pdf.line(MARGIN, yPos, PAGE_WIDTH - MARGIN, yPos);

    // Invoice Details Section (2 columns)
    yPos += 12;
    const leftColX = MARGIN;
    const rightColX = PAGE_WIDTH - MARGIN - 40; // Increase gap between columns
    let leftY = yPos;
    let rightY = yPos;
    const rowGap = 7;
    // Left: Bill To, client info, Invoice number
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('BILL TO', leftColX, leftY);
    leftY += rowGap + 1;
    pdf.setFontSize(14);
    pdf.text(safeText(invoice.client?.name), leftColX, leftY);
    leftY += rowGap + 2;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(12);
    if (invoice.client?.address) {
      const addressLines = safeText(invoice.client.address).split('\n');
      addressLines.forEach(line => {
        if (line.trim()) {
          pdf.text(line.trim(), leftColX, leftY);
          leftY += 5;
        }
      });
    }
    if (invoice.client?.phone) {
      pdf.text(`Phone: ${safeText(invoice.client.phone)}`, leftColX, leftY);
      leftY += 5;
    }
    if (invoice.client?.email) {
      pdf.text(safeText(invoice.client.email), leftColX, leftY);
      leftY += 5;
    }
    leftY += 2;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.text('INVOICE', leftColX, leftY);
    leftY += rowGap;
    pdf.setFontSize(16);
    pdf.setTextColor(...brandRGB);
    pdf.text(safeText(invoice.invoiceNumber), leftColX, leftY);
    pdf.setTextColor(0);
    // Right: Date, Due Date, Balance Due
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.text('DATE', rightColX, rightY);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.text(new Date(invoice.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }), rightColX, rightY + rowGap);
    rightY += rowGap * 2;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.text('DUE DATE', rightColX, rightY);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(11);
    pdf.text(new Date(invoice.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }), rightColX, rightY + rowGap);
    rightY += rowGap * 2;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.text('BALANCE DUE', rightColX, rightY);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(...brandRGB);
    pdf.text(formatCurrency(invoice.status === 'paid' ? 0 : invoice.total, currency), rightColX, rightY + rowGap);
    pdf.setTextColor(0);
    // Set yPos to the greater of leftY and rightY before starting the items table
    yPos = Math.max(leftY, rightY + rowGap * 2) + 12;

    // Items Table (unchanged, but use correct currency)
    const tableTop = yPos;
    pdf.setFillColor(...brandRGB);
    pdf.rect(MARGIN, tableTop, CONTENT_WIDTH, 10, 'F');
    pdf.setTextColor(255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text('DESCRIPTION', MARGIN + 2, tableTop + 7.5);
    pdf.text('RATE', MARGIN + 100, tableTop + 7.5);
    pdf.text('QTY', MARGIN + 130, tableTop + 7.5);
    pdf.text('AMOUNT', MARGIN + 150, tableTop + 7.5);
    pdf.setTextColor(0);
    // Table Body
    let currentY = tableTop + 14;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    invoice.items.forEach((item, index) => {
      if (index % 2 === 1) {
        pdf.setFillColor(248, 250, 252);
        pdf.rect(MARGIN, currentY - 2, CONTENT_WIDTH, 10, 'F');
      }
      const description = safeText(item.description);
      const descLines = pdf.splitTextToSize(description, 80);
      const descHeight = descLines.length * 3;
      pdf.text(descLines, MARGIN + 2, currentY + 3);
      pdf.text(formatCurrency(item.rate, currency), MARGIN + 100, currentY + 3);
      pdf.text(item.quantity.toString(), MARGIN + 130, currentY + 3);
      pdf.text(formatCurrency(item.amount, currency), MARGIN + 150, currentY + 3);
      currentY += Math.max(12, descHeight + 2);
    });
    // Totals Section (unchanged, but use correct currency)
    currentY += 8;
    const totalsX = MARGIN + 100;
    const totalsWidth = 65;
    pdf.setFont('helvetica', 'normal');
    pdf.text('Subtotal:', totalsX, currentY);
    pdf.text(formatCurrency(invoice.subtotal, currency), totalsX + totalsWidth, currentY, { align: 'right' });
    // Dashed line below Subtotal
    pdf.setLineWidth(0.2);
    pdf.setDrawColor(150);
    for (let x = totalsX; x < totalsX + totalsWidth; x += 3) {
      pdf.line(x, currentY + 2, x + 1.5, currentY + 2);
    }
    currentY += 6;
    pdf.text(`Tax (${invoice.taxRate}%):`, totalsX, currentY);
    pdf.text(formatCurrency(invoice.taxAmount, currency), totalsX + totalsWidth, currentY, { align: 'right' });
    // Dashed line below Tax
    for (let x = totalsX; x < totalsX + totalsWidth; x += 3) {
      pdf.line(x, currentY + 2, x + 1.5, currentY + 2);
    }
    if (invoice.discountAmount > 0) {
      currentY += 6;
      pdf.setTextColor(34, 197, 94);
      pdf.text('Discount:', totalsX, currentY);
      pdf.text(`-${formatCurrency(invoice.discountAmount, currency)}`, totalsX + totalsWidth, currentY, { align: 'right' });
      pdf.setTextColor(0);
      // Dashed line below Discount
      for (let x = totalsX; x < totalsX + totalsWidth; x += 3) {
        pdf.line(x, currentY + 2, x + 1.5, currentY + 2);
      }
    }
    currentY += 8;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...brandRGB);
    pdf.text('TOTAL:', totalsX, currentY);
    pdf.text(formatCurrency(invoice.total, currency), totalsX + totalsWidth, currentY, { align: 'right' });
    // Dashed line below Total
    pdf.setTextColor(0);
    for (let x = totalsX; x < totalsX + totalsWidth; x += 3) {
      pdf.line(x, currentY + 2, x + 1.5, currentY + 2);
    }
    pdf.setTextColor(0);
    if (invoice.status === 'paid') {
      currentY += 6;
      pdf.setTextColor(34, 197, 94);
      pdf.text('PAID:', totalsX, currentY);
      pdf.text(`-${formatCurrency(invoice.total, currency)}`, totalsX + totalsWidth, currentY, { align: 'right' });
      pdf.setTextColor(0);
      // Dashed line below Paid
      for (let x = totalsX; x < totalsX + totalsWidth; x += 3) {
        pdf.line(x, currentY + 2, x + 1.5, currentY + 2);
      }
    }
    currentY += 6;
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...brandRGB);
    const balanceDue = invoice.status === 'paid' ? 0 : invoice.total;
    pdf.text('BALANCE DUE:', totalsX, currentY);
    pdf.text(formatCurrency(balanceDue, currency), totalsX + totalsWidth, currentY, { align: 'right' });
    pdf.setTextColor(0);
    // Dashed line below Balance Due
    for (let x = totalsX; x < totalsX + totalsWidth; x += 3) {
      pdf.line(x, currentY + 2, x + 1.5, currentY + 2);
    }
    // Payment Link, Notes, Terms, Footer remain unchanged

    // Status Stamp
    if (invoice.status === 'paid' || invoice.status === 'overdue') {
      try {
        const stampUrl = `${VITE_APP_URL}/${invoice.status === 'paid' ? 'unpaid' : invoice.status.toLowerCase()}.png`;
        console.debug('[PDF] Attempting to load status stamp:', stampUrl);
        const stampData = await loadImage(stampUrl);
        
        pdf.addImage(
          stampData.objectUrl,
          'PNG',
          MARGIN,
          currentY - 25,
          50,
          25
        );
        
        URL.revokeObjectURL(stampData.objectUrl);
      } catch (error) {
        console.warn('[PDF] Failed to load status stamp:', error);
      }
    }

    // Payment Link
    if (invoice.status !== 'paid' && invoice.paymentLink) {
      currentY += 10; // reduced from 20
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...brandRGB);
      pdf.text('Pay Online', MARGIN, currentY);
      pdf.setFont('helvetica', 'normal');
      currentY += 4;
      pdf.setTextColor(59, 130, 246); // Blue for link
      const linkLines = pdf.splitTextToSize(safeText(invoice.paymentLink), CONTENT_WIDTH - 10);
      pdf.text(linkLines, MARGIN, currentY);
      currentY += linkLines.length * 4;
      pdf.setTextColor(0);
      currentY += 4;
      pdf.setFontSize(7);
      pdf.setTextColor(107, 114, 128); // Gray
      pdf.text('Copy the URL above to your browser to make a secure payment', MARGIN, currentY);
      pdf.setTextColor(0);
    }

    // Notes
    if (invoice.notes) {
      currentY += 4; // reduced from 15
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Notes:', MARGIN, currentY);
      currentY += 4;
      pdf.setFont('helvetica', 'normal');
      const notesLines = pdf.splitTextToSize(safeText(invoice.notes), CONTENT_WIDTH);
      pdf.text(notesLines, MARGIN, currentY);
      currentY += notesLines.length * 3;
    }

    // Terms
    if (invoice.terms) {
      currentY += 8;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Terms & Conditions:', MARGIN, currentY);
      currentY += 4;
      pdf.setFont('helvetica', 'normal');
      const termsLines = pdf.splitTextToSize(safeText(invoice.terms), CONTENT_WIDTH);
      pdf.text(termsLines, MARGIN, currentY);
      currentY += termsLines.length * 3;
    }

    // Dynamic gap before footer (removed, now handled by minFooterY)

    // Ensure enough space before footer so 'Thank you' is always visible
    const minFooterY = PAGE_HEIGHT - 50; // elevate footer higher above page bottom
    if (currentY < minFooterY) {
      currentY = minFooterY;
    }

    // Footer
    pdf.setDrawColor(0);
    pdf.setLineWidth(0.5);
    pdf.line(MARGIN, currentY, PAGE_WIDTH - MARGIN, currentY);
    currentY += 10;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...brandRGB);
    pdf.text('Thank you for your business!', PAGE_WIDTH / 2, currentY, { align: 'center' });
    pdf.setTextColor(0);
    currentY += 5;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(companyName, PAGE_WIDTH / 2, currentY, { align: 'center' });

    return pdf;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
}

// Modern template: match the ModernInvoicePreview exactly
async function modernInvoiceTemplate(invoice: Invoice, workspace: Workspace): Promise<jsPDF> {
  const pdf = new jsPDF({ format: 'a4', unit: 'mm', orientation: 'portrait' });
  const company = workspace.settings?.branding || {};
  const brandColor = company.primaryColor || '#2563EB';
  // Convert brandColor hex to RGB
  const hexToRgb = (hex: string): [number, number, number] => {
    let c = hex.replace('#', '');
    if (c.length === 3) c = c.split('').map(x => x + x).join('');
    const num = parseInt(c, 16);
    return [num >> 16, (num >> 8) & 255, num & 255];
  };
  const brandRGB: [number, number, number] = hexToRgb(brandColor);
  // Header bar
  pdf.setFillColor(...brandRGB);
  pdf.rect(0, 0, 210, 20, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(22);
  pdf.setTextColor(255);
  pdf.text(company.companyName || workspace.name, 20, 13);
  pdf.setFontSize(14);
  pdf.text('INVOICE', 170, 10, { align: 'right' });
  pdf.setFontSize(12);
  pdf.text(invoice.invoiceNumber, 170, 16, { align: 'right' });
  pdf.setTextColor(0);
  let y = 28;
  // Company info
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  if (company.licenseNumber) {
    pdf.text(`License: ${company.licenseNumber}`, 20, y);
    y += 5;
  }
  if (company.address) {
    const addressLines = company.address.split('\n');
    addressLines.forEach(line => {
      if (line.trim()) {
        pdf.text(line.trim(), 20, y);
        y += 5;
      }
    });
  }
  if (company.phone) {
    pdf.text(`Phone: ${company.phone}`, 20, y);
    y += 5;
  }
  if (company.email) {
    pdf.text(company.email, 20, y);
    y += 5;
  }
  // Invoice details right
  pdf.text(`Date: ${new Date(invoice.issueDate).toLocaleDateString('en-GB')}`, 170, 28, { align: 'right' });
  pdf.text(`Due: ${new Date(invoice.dueDate).toLocaleDateString('en-GB')}`, 170, 33, { align: 'right' });
  // Bill To
  y += 10;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text('Bill To', 20, y);
  y += 7;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.text(invoice.client.name, 20, y);
  y += 7;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);
  if (invoice.client.address) {
    const clientAddressLines = invoice.client.address.split('\n');
    clientAddressLines.forEach(line => {
      if (line.trim()) {
        pdf.text(line.trim(), 20, y);
        y += 5;
      }
    });
  }
  if (invoice.client.phone) { pdf.text(`Phone: ${invoice.client.phone}`, 20, y); y += 5; }
  if (invoice.client.email) { pdf.text(invoice.client.email, 20, y); y += 5; }
  // Items Table
  y += 10;
  pdf.setFillColor(...brandRGB);
  pdf.rect(20, y, 170, 8, 'F');
  pdf.setTextColor(255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text('DESCRIPTION', 22, y + 6);
  pdf.text('RATE', 110, y + 6);
  pdf.text('QTY', 140, y + 6);
  pdf.text('AMOUNT', 170, y + 6, { align: 'right' });
  pdf.setTextColor(0);
  y += 10;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  invoice.items.forEach((item, idx) => {
    if (idx % 2 === 1) {
      pdf.setFillColor(239, 246, 255);
      pdf.rect(20, y - 2, 170, 8, 'F');
    }
    pdf.text(item.description, 22, y + 4);
    pdf.text(String(item.rate), 110, y + 4);
    pdf.text(String(item.quantity), 140, y + 4);
    pdf.text(String(item.amount), 170, y + 4, { align: 'right' });
    y += 8;
  });
  // Totals
  y += 8;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(...brandRGB);
  pdf.text('Total', 110, y);
  pdf.text(String(invoice.total), 170, y, { align: 'right' });
  pdf.setTextColor(0);
  // Thank you
  y += 16;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);
  pdf.text('Thank you for your business!', 105, y, { align: 'center' });
  // Signature
  if (invoice.signature) {
    y += 18;
    pdf.setDrawColor(200);
    pdf.line(60, y, 150, y);
    y += 4;
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(12);
    if (invoice.signature.startsWith('http')) {
      pdf.addImage(invoice.signature, 'PNG', 90, y, 30, 12);
    } else {
      pdf.text(invoice.signature, 105, y + 8, { align: 'center' });
    }
  }
  return pdf;
}

// Minimal template: match the MinimalInvoicePreview exactly
async function minimalInvoiceTemplate(invoice: Invoice, workspace: Workspace): Promise<jsPDF> {
  const pdf = new jsPDF({ format: 'a4', unit: 'mm', orientation: 'portrait' });
  const company = workspace.settings?.branding || {};
  pdf.setFont('courier', 'normal');
  pdf.setFontSize(18);
  pdf.text(company.companyName || workspace.name, 20, 20);
  pdf.setFontSize(10);
  let y = 28;
  pdf.text(company.address || '', 20, y);
  y += 5;
  pdf.text(`Phone: ${company.phone || ''}`, 20, y);
  y += 5;
  pdf.text(company.email || '', 20, y);
  y += 8;
  pdf.setDrawColor(180);
  pdf.setLineWidth(0.5);
  pdf.setLineDashPattern([2, 2], 0);
  pdf.line(20, y, 190, y);
  y += 8;
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(12);
  pdf.text('Invoice', 20, y);
  pdf.setFont('courier', 'normal');
  pdf.setFontSize(11);
  pdf.text(invoice.invoiceNumber, 40, y);
  pdf.text('Date:', 120, y);
  pdf.text(new Date(invoice.issueDate).toLocaleDateString('en-GB'), 140, y);
  y += 7;
  pdf.text('Due:', 120, y);
  pdf.text(new Date(invoice.dueDate).toLocaleDateString('en-GB'), 140, y);
  y += 10;
  pdf.setFont('courier', 'bold');
  pdf.text('Bill To', 20, y);
  pdf.setFont('courier', 'normal');
  pdf.text(invoice.client.name, 40, y);
  y += 5;
  pdf.text(invoice.client.address || '', 40, y);
  y += 5;
  pdf.text(invoice.client.phone || '', 40, y);
  y += 5;
  pdf.text(invoice.client.email || '', 40, y);
  y += 8;
  pdf.setDrawColor(180);
  pdf.setLineDashPattern([2, 2], 0);
  pdf.line(20, y, 190, y);
  y += 8;
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(10);
  pdf.text('Description', 22, y);
  pdf.text('Rate', 110, y);
  pdf.text('Qty', 140, y);
  pdf.text('Amount', 170, y, { align: 'right' });
  y += 5;
  pdf.setFont('courier', 'normal');
  pdf.setFontSize(10);
  invoice.items.forEach((item, idx) => {
    if (idx % 2 === 0) {
      pdf.setFillColor(245, 245, 245);
      pdf.rect(20, y - 3, 170, 5, 'F');
    }
    pdf.text(item.description, 22, y);
    pdf.text(String(item.rate), 110, y);
    pdf.text(String(item.quantity), 140, y);
    pdf.text(String(item.amount), 170, y, { align: 'right' });
    y += 5;
  });
  y += 8;
  pdf.setDrawColor(180);
  pdf.setLineDashPattern([2, 2], 0);
  pdf.line(20, y, 190, y);
  y += 8;
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(12);
  pdf.text('Total:', 120, y);
  pdf.text(String(invoice.total), 170, y, { align: 'right' });
  y += 16;
  pdf.setFont('courier', 'normal');
  pdf.setFontSize(10);
  pdf.text('Thank you!', 105, y, { align: 'center' });
  // Signature
  if (invoice.signature) {
    y += 14;
    pdf.setDrawColor(200);
    pdf.line(60, y, 150, y);
    y += 4;
    pdf.setFont('courier', 'italic');
    pdf.setFontSize(12);
    if (invoice.signature.startsWith('http')) {
      pdf.addImage(invoice.signature, 'PNG', 90, y, 30, 10);
    } else {
      pdf.text(invoice.signature, 105, y + 8, { align: 'center' });
    }
  }
  return pdf;
}

// Bold template: match the BoldInvoicePreview exactly
async function boldInvoiceTemplate(invoice: Invoice, workspace: Workspace): Promise<jsPDF> {
  const pdf = new jsPDF({ format: 'a4', unit: 'mm', orientation: 'portrait' });
  const company = workspace.settings?.branding || {};
  const brandColor = company.primaryColor || '#B91C1C';
  // Convert brandColor hex to RGB
  const hexToRgb = (hex: string): [number, number, number] => {
    let c = hex.replace('#', '');
    if (c.length === 3) c = c.split('').map(x => x + x).join('');
    const num = parseInt(c, 16);
    return [num >> 16, (num >> 8) & 255, num & 255];
  };
  const brandRGB: [number, number, number] = hexToRgb(brandColor);
  // Header
  pdf.setFillColor(...brandRGB);
  pdf.rect(0, 0, 210, 25, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(28);
  pdf.setTextColor(255);
  pdf.text('INVOICE', 105, 17, { align: 'center' });
  pdf.setTextColor(0);
  let y = 35;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(22);
  pdf.setTextColor(...brandRGB);
  pdf.text(`#${invoice.invoiceNumber}`, 20, y);
  pdf.setTextColor(0);
  pdf.setFontSize(14);
  y += 10;
  pdf.text(company.companyName || workspace.name, 20, y);
  y += 6;
  pdf.setFontSize(10);
  if (company.address) {
    const addressLines = company.address.split('\n');
    addressLines.forEach(line => {
      if (line.trim()) {
        pdf.text(line.trim(), 20, y);
        y += 5;
      }
    });
  }
  if (company.phone) {
    pdf.text(`Phone: ${company.phone}`, 20, y);
    y += 5;
  }
  if (company.email) {
    pdf.text(company.email, 20, y);
    y += 5;
  }
  y += 12;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text('Bill To:', 20, y);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(12);
  pdf.text(invoice.client.name, 40, y);
  y += 6;
  if (invoice.client.address) {
    const clientAddressLines = invoice.client.address.split('\n');
    clientAddressLines.forEach(line => {
      if (line.trim()) {
        pdf.text(line.trim(), 40, y);
        y += 5;
      }
    });
  }
  if (invoice.client.phone) {
    pdf.text(invoice.client.phone, 40, y);
    y += 5;
  }
  if (invoice.client.email) {
    pdf.text(invoice.client.email, 40, y);
    y += 5;
  }
  y += 10;
  // Items Table
  pdf.setFillColor(...brandRGB);
  pdf.rect(20, y, 170, 10, 'F');
  pdf.setTextColor(255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text('DESCRIPTION', 22, y + 7);
  pdf.text('RATE', 110, y + 7);
  pdf.text('QTY', 140, y + 7);
  pdf.text('AMOUNT', 170, y + 7, { align: 'right' });
  pdf.setTextColor(0);
  y += 12;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);
  invoice.items.forEach((item, idx) => {
    if (idx % 2 === 0) {
      pdf.setFillColor(245, 245, 245);
      pdf.rect(20, y - 3, 170, 8, 'F');
    }
    pdf.text(item.description, 22, y + 4);
    pdf.text(String(item.rate), 110, y + 4);
    pdf.text(String(item.quantity), 140, y + 4);
    pdf.text(String(item.amount), 170, y + 4, { align: 'right' });
    y += 8;
  });
  y += 10;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setDrawColor(...brandRGB);
  pdf.setLineWidth(1.5);
  pdf.rect(110, y, 80, 20, 'D');
  pdf.setTextColor(...brandRGB);
  pdf.text('Total', 120, y + 10);
  pdf.text(String(invoice.total), 180, y + 10, { align: 'right' });
  pdf.setTextColor(0);
  y += 30;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('Thank you!', 105, y, { align: 'center' });
  // Signature
  if (invoice.signature) {
    y += 18;
    pdf.setDrawColor(...brandRGB);
    pdf.line(60, y, 150, y);
    y += 4;
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(13);
    if (invoice.signature.startsWith('http')) {
      pdf.addImage(invoice.signature, 'PNG', 90, y, 30, 12);
    } else {
      pdf.text(invoice.signature, 105, y + 8, { align: 'center' });
    }
  }
  return pdf;
}

// Elegant template: match the ElegantInvoicePreview exactly
async function elegantInvoiceTemplate(invoice: Invoice, workspace: Workspace): Promise<jsPDF> {
  const pdf = new jsPDF({ format: 'a4', unit: 'mm', orientation: 'portrait' });
  const company = workspace.settings?.branding || {};
  // Use brandColor for gold accent if provided, else fallback to gold
  const brandColor = company.primaryColor || '#D4AF37';
  const hexToRgb = (hex: string): [number, number, number] => {
    let c = hex.replace('#', '');
    if (c.length === 3) c = c.split('').map(x => x + x).join('');
    const num = parseInt(c, 16);
    return [num >> 16, (num >> 8) & 255, num & 255];
  };
  const gold = hexToRgb(brandColor);
  pdf.setFont('times', 'italic');
  pdf.setFontSize(26);
  pdf.setTextColor(...gold);
  pdf.text(company.companyName || workspace.name, 200, 22, { align: 'right' });
  pdf.setTextColor(0);
  let y = 32;
  pdf.setFont('times', 'normal');
  pdf.setFontSize(11);
  if (company.address) {
    const addressLines = company.address.split('\n');
    addressLines.forEach(line => {
      if (line.trim()) {
        pdf.text(line.trim(), 200, y, { align: 'right' });
        y += 6;
      }
    });
  }
  if (company.phone) {
    pdf.text(`Phone: ${company.phone}`, 200, y, { align: 'right' });
    y += 6;
  }
  if (company.email) {
    pdf.text(company.email, 200, y, { align: 'right' });
    y += 6;
  }
  y += 4;
  pdf.setDrawColor(...gold);
  pdf.setLineWidth(1);
  pdf.line(20, y, 190, y);
  y += 10;
  pdf.setFont('times', 'italic');
  pdf.setFontSize(14);
  pdf.setTextColor(...gold);
  pdf.text('Invoice', 20, y);
  pdf.setTextColor(0);
  pdf.setFont('times', 'normal');
  pdf.setFontSize(13);
  pdf.text(invoice.invoiceNumber, 50, y);
  pdf.text('Date:', 120, y);
  pdf.text(new Date(invoice.issueDate).toLocaleDateString('en-GB'), 140, y);
  y += 7;
  pdf.text('Due:', 120, y);
  pdf.text(new Date(invoice.dueDate).toLocaleDateString('en-GB'), 140, y);
  y += 10;
  pdf.setFont('times', 'italic');
  pdf.setFontSize(13);
  pdf.setTextColor(...gold);
  pdf.text('Bill To', 20, y);
  pdf.setTextColor(0);
  pdf.setFont('times', 'normal');
  pdf.setFontSize(12);
  pdf.text(invoice.client.name, 50, y);
  y += 6;
  if (invoice.client.address) {
    const clientAddressLines = invoice.client.address.split('\n');
    clientAddressLines.forEach(line => {
      if (line.trim()) {
        pdf.text(line.trim(), 50, y);
        y += 5;
      }
    });
  }
  if (invoice.client.phone) {
    pdf.text(invoice.client.phone, 50, y);
    y += 5;
  }
  if (invoice.client.email) {
    pdf.text(invoice.client.email, 50, y);
    y += 5;
  }
  y += 5;
  pdf.setDrawColor(...gold);
  pdf.setLineWidth(0.7);
  pdf.line(20, y, 190, y);
  y += 7;
  pdf.setFont('times', 'italic');
  pdf.setFontSize(12);
  pdf.setTextColor(...gold);
  pdf.text('Description', 22, y);
  pdf.text('Rate', 110, y);
  pdf.text('Qty', 140, y);
  pdf.text('Amount', 170, y, { align: 'right' });
  y += 5;
  pdf.setFont('times', 'normal');
  pdf.setFontSize(11);
  invoice.items.forEach((item, _idx) => {
    pdf.text(item.description, 22, y);
    pdf.text(String(item.rate), 110, y);
    pdf.text(String(item.quantity), 140, y);
    pdf.text(String(item.amount), 170, y, { align: 'right' });
    y += 5;
  });
  y += 10;
  pdf.setDrawColor(...gold);
  pdf.setLineWidth(0.7);
  pdf.line(20, y, 190, y);
  y += 7;
  pdf.setFont('times', 'italic');
  pdf.setFontSize(13);
  pdf.setTextColor(...gold);
  pdf.text('Total:', 120, y);
  pdf.text(String(invoice.total), 170, y, { align: 'right' });
  pdf.setTextColor(0);
  y += 16;
  pdf.setFont('times', 'italic');
  pdf.setFontSize(11);
  pdf.text('Signature: ___________________________', 190, y, { align: 'right' });
  // Signature
  if (invoice.signature) {
    y += 14;
    pdf.setDrawColor(...gold);
    pdf.line(60, y, 150, y);
    y += 4;
    pdf.setFont('times', 'italic');
    pdf.setFontSize(13);
    if (invoice.signature.startsWith('http')) {
      pdf.addImage(invoice.signature, 'PNG', 90, y, 30, 12);
    } else {
      pdf.setTextColor(...gold);
      pdf.text(invoice.signature, 105, y + 8, { align: 'center' });
      pdf.setTextColor(0);
    }
  }
  return pdf;
}

// Register all templates
export const invoicePDFTemplates: Record<string, InvoicePDFTemplate> = {
  default: defaultInvoiceTemplate,
  modern: modernInvoiceTemplate,
  minimal: minimalInvoiceTemplate,
  bold: boldInvoiceTemplate,
  elegant: elegantInvoiceTemplate,
};

// Main generator: accepts template name, returns download URL as before
export const generateInvoicePDF = async (
  invoice: Invoice,
  workspace: Workspace,
  template: string = 'default'
): Promise<string> => {
  const templateFn = invoicePDFTemplates[template] || invoicePDFTemplates['default'];
  const pdf = await templateFn(invoice, workspace);

  // Generate PDF blob
  const pdfBlob = pdf.output('blob');

  // Create a safe filename
  const safeClientName = invoice.client?.name
    ?.toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '') || 'unnamed_client';

  // Upload to Firebase Storage
  const fileName = `invoices/${workspace.name}/${safeClientName}/Invoice_${invoice.invoiceNumber}.pdf`;
  const storageRef = ref(storage, fileName);
  
  const metadata = {
    contentType: 'application/pdf',
    cacheControl: 'no-cache',
    customMetadata: {
      clientName: invoice.client?.name || '',
      invoiceNumber: invoice.invoiceNumber,
      issueDate: invoice.issueDate
    }
  };

  await uploadBytes(storageRef, pdfBlob, metadata);
  
  // Get download URL
  const downloadUrl = await getDownloadURL(storageRef);
  window.open(downloadUrl, '_blank');
  return downloadUrl;
};

export const downloadInvoicePDF = async (storagePath: string, fileName: string): Promise<void> => {
  try {
    const storageRef = ref(storage, storagePath);
    const downloadUrl = await getDownloadURL(storageRef);
    
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
};